import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators, FormArray } from "@angular/forms";
import { ConfigApi } from "src/app/config-api";
import { ApiCallingService } from "src/app/services/api-calling.service";

@Component({
  selector: "app-membership-form",
  templateUrl: "./membership-form.component.html",
  styleUrls: ["./membership-form.component.css"],
})
export class MembershipFormComponent implements OnInit, OnChanges {
  membershipForm!: FormGroup;
  @Output() membershipCreated = new EventEmitter<any>();
  @Input() membershipData: any;
  @Input() isEditMode: boolean = false;
  isSubmitting: boolean = false;
  isSubmitAttempted: boolean = false;

  periods = ["week", "month", "year"];
  inclusiveOptions = ["Inclusive", "Exclusive"];
  userDetails: any;

  constructor(
    private fb: FormBuilder,
    private apiCallingService: ApiCallingService
  ) {
    let data = localStorage.getItem("Auth-user");
    if (!data) {
      return;
    }
    try {
      this.userDetails = JSON.parse(data);
    } catch (error) {
      console.error(error);
    }
  }

  ngOnInit(): void {
    this.membershipForm = this.fb.group({
      name: ["", [Validators.required, Validators.maxLength(50)]],
      description: ["", [Validators.required, Validators.maxLength(200)]],
      timing: ["", Validators.required],
      duration: [1, [Validators.required, Validators.min(1)]],
      price: [null, [Validators.required, Validators.min(0)]],
      vat: [
        null,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      isExclusive: [false, Validators.required],
      discount: [0, [Validators.min(0), Validators.max(100)]],
      freeShipment: [false, Validators.required], // Default to false (No)
      freeProduct: [0, [Validators.min(0)]],
      features: this.fb.array([]),
    });

    if (this.membershipData) {
      this.isEditMode = !!this.membershipData?._id;
      this.patchForm(this.membershipData);
      this.setEditModeRestrictions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["membershipData"] &&
      changes["membershipData"].currentValue &&
      this.membershipForm
    ) {
      this.isEditMode = !!this.membershipData?._id;
      this.patchForm(this.membershipData);
      this.setEditModeRestrictions();
    }
  }

  private setEditModeRestrictions() {
    if (this.isEditMode) {
      this.membershipForm.get("timing")?.disable();
      this.membershipForm.get("duration")?.disable();
      this.membershipForm.get("price")?.disable();
      this.membershipForm.get("vat")?.disable();
      this.membershipForm.get("isExclusive")?.disable();
      this.membershipForm.get("discount")?.disable();
      this.membershipForm.get("freeShipment")?.disable();
      this.membershipForm.get("freeProduct")?.disable();
    }
  }

  private patchForm(data: any) {
    this.membershipForm.patchValue({
      name: data?.name || "",
      description: data?.description || "",
      timing: data?.timing || "",
      duration: data?.duration || 1,
      price: data?.chargeableAmount / 100 || null,
      vat: data?.vat || null,
      isExclusive: data?.isExclusive === true,
      discount: data?.discount || 0,
      freeShipment: data?.freeShipment ?? false,
      freeProduct: data?.freeProduct || 0,
    });

    this.features.clear();
    if (data?.features?.length) {
      try {
        const featureArr =
          typeof data.features === "string"
            ? JSON.parse(data?.features)
            : data?.features;
        featureArr.forEach((f: string) => this.addFeature(f));
      } catch (e) {
        console.error("Invalid features format", e);
      }
    }
  }

  get features(): FormArray {
    return this.membershipForm.get("features") as FormArray;
  }

  addFeature(feature: string) {
    if (feature) this.features.push(this.fb.control(feature));
  }

  removeFeature(index: number) {
    this.features.removeAt(index);
  }

  get hasNoFeatures(): boolean {
    return this.features.length === 0;
  }

  get shouldShowFeatureError(): boolean {
    return this.isSubmitAttempted && this.hasNoFeatures;
  }

  onSubmit() {
    this.isSubmitAttempted = true;

    if (this.membershipForm.valid && this.features.length > 0) {
      const formValue = this.membershipForm.getRawValue();

      const payload = {
        ...formValue,
        operator_id: this.userDetails?._id,
        features: JSON.stringify(formValue.features),
      };

      const apiUrl = this.isEditMode
        ? `${ConfigApi.URLS.MEMBERSHIP.UPDATE}/${this.membershipData._id}`
        : ConfigApi.URLS.MEMBERSHIP.CREATE;

      const method = this.isEditMode ? "PUT" : "POST";

      this.isSubmitting = true;

      this.apiCallingService.callAPI(payload, method, apiUrl).subscribe(
        (res) => {
          this.isSubmitting = false;
          if (this.apiCallingService.handleSuccess(res)) {
            console.log("Membership created successfully:", res);
            this.membershipForm.reset();
            this.features.clear();
            this.isSubmitAttempted = false;
            this.membershipCreated.emit(res.data);
          }
        },
        (error) => {
          this.isSubmitting = false;
          console.log(
            "🚀 ~ MembershipFormComponent ~ onSubmit ~ error:",
            error
          );
        }
      );
    } else {
      this.membershipForm.markAllAsTouched();
    }
  }
}
